import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Zap, Search, X } from "lucide-react";
import Paywall from "../components/Paywall.jsx";
import { useIsPremium } from "../lib/premium";

export default function QuickReference() {
  const premium = useIsPremium();
  const [panels, setPanels] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  useEffect(() => { if (premium) api.get("/quick-reference").then((r) => setPanels(r.data)).catch(() => {}); }, [premium]);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(panels.map((p) => p.category).filter(Boolean))).sort()],
    [panels]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return panels.filter((p) => {
      if (category !== "All" && p.category !== category) return false;
      if (!q) return true;
      if ((p.title || "").toLowerCase().includes(q)) return true;
      if ((p.category || "").toLowerCase().includes(q)) return true;
      return (p.items || []).some(
        (i) =>
          (i.label || "").toLowerCase().includes(q) ||
          (i.value || "").toLowerCase().includes(q) ||
          (i.note || "").toLowerCase().includes(q)
      );
    });
  }, [panels, query, category]);

  if (!premium) return <Paywall feature="Quick Reference panels" />;

  return (
    <div className="space-y-6" data-testid="quickref-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">At-a-glance</div>
        <h1 className="font-heading text-3xl font-bold text-[#111827] mt-1 flex items-center gap-2">
          <Zap className="w-7 h-7 text-[#111827]" /> Quick Reference
        </h1>
        <p className="text-slate-600 text-sm mt-1">Vital signs, drug infusions, emergencies, lab normals, paediatrics and other bedside tools.</p>
      </div>

      {/* Search + category filter */}
      <div className="space-y-3">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search panels (e.g. DKA, sepsis, potassium)…"
            data-testid="quickref-search"
            className="w-full rounded-lg border border-slate-300 pl-9 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111827]"
          />
          {query && (
            <button onClick={() => setQuery("")} data-testid="quickref-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              data-testid={`quickref-cat-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                category === c ? "bg-[#0B5FFF] text-white border-[#0B5FFF]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500" data-testid="quickref-count">{filtered.length} of {panels.length} panels</p>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="quickref-empty">
          No panels match “{query}”. Try a different term or category.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((p) => (
            <Card key={p.ref_id} className="border-slate-200" data-testid={`qr-card-${p.ref_id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <Badge className="bg-slate-100 text-slate-700 border-0">{p.category}</Badge>
                  <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: (p.color || "#111827") + "20", color: p.color || "#111827" }}>
                    <Zap className="w-4 h-4" />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-[#111827]">{p.title}</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {(p.items || []).map((i, n) => (
                    <li key={n} className="border-b border-slate-100 pb-2 last:border-b-0 last:pb-0">
                      <div className="font-medium text-[#111827]">{i.label}</div>
                      <div className="text-slate-700">{i.value}</div>
                      {i.note && <div className="text-xs text-slate-500 mt-0.5">{i.note}</div>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
